import { useLazyQuery } from '@apollo/client/react'
import { isEmpty } from 'lodash-es'
import { useEffect, useRef, useState } from 'react'

import { gql } from '../../__generated__/graphql/catalog/gql'
import { useGoogleTagManager } from '../../lib/hooks/useGoogleTagManager'

type Props = {
  viewHandle: string
  skusViewed: string[]
  trackBatchNumber?: boolean
  resetKey?: string
}

/**
 * Request variant information & track product impressions for all of the items in a collection.
 *
 * This (essentially) stateless hook exists to avoid unnecessary rerenders for a collection page.
 * By encapsulating the data loading & tracking behavior associated with tracking product impressions,
 * we are able to avoid re-renders for functionality that is not related  to the presentation of the collections page.
 *
 * @argument trackBatchNumber (optional, default false) - when true, each distinct `view_item_list`
 *   push is stamped with a batch number. The first push is batch 1, each subsequent push
 *   (e.g. from "load more") increments. Opt-in so existing callers are unaffected.
 * @argument resetKey (optional) - when this value changes, the dedupe set and batch counter reset,
 *   starting a fresh sequence at batch 1. Used to treat a new search query / filter change as a new
 *   list. When omitted, no reset occurs (preserving existing collection-page behavior).
 */
export function useCollectionViewProductImpressions({
  viewHandle,
  skusViewed,
  trackBatchNumber = false,
  resetKey,
}: Props) {
  const { pushProductImpressions } = useGoogleTagManager()
  const [skusSent, _] = useState(() => {
    return new Set<string>()
  })
  const batchNumberRef = useRef(0)

  // Declared BEFORE the impressions effect so that, on the render where `resetKey` changes, the
  // dedupe set is cleared first; the impressions effect then re-diffs the new list against an
  // empty set and emits it as a fresh batch 1.
  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (resetKey === undefined) return // No reset key provided: preserve existing behavior.
    skusSent.clear()
    batchNumberRef.current = 0
  }, [resetKey, skusSent])

  const [fetchVariantsForSkus] = useLazyQuery(
    gql(`
      query GetVariantsForImpressionEvents($skus: [String!]!) {
        productCatalogVariants(skus: $skus) {
          id
          sku
          shopifyId
          shopifyProductId
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
          }
          group {
            id
            primaryCategory {
              id
            }
            product {
              id
              handle
            }
          }
        }
      }
    `)
  )

  useEffect(() => {
    const skusToSend: string[] = []
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-live-state-to-parent
    skusViewed.forEach(sku => {
      if (!skusSent.has(sku)) {
        skusToSend.push(sku)
        skusSent.add(sku)
      }
    })

    const handleProductImpressions = async () => {
      // Query to get the default variants for the items in a given collection/sectionId
      const { data } = await fetchVariantsForSkus({
        variables: {
          skus: skusToSend,
        },
      })

      // If we receive items from the query, push product impressions
      if (data && !isEmpty(data.productCatalogVariants)) {
        // Increment only when a real push fires
        batchNumberRef.current += 1
        pushProductImpressions({
          variants: data.productCatalogVariants.map(variant => {
            return {
              ...variant,
              product: variant.group.product,
              variantGroup: variant.group,
            }
          }),
          list: viewHandle,
          batchNumber: trackBatchNumber ? batchNumberRef.current : undefined,
        })
      }
    }

    if (!isEmpty(skusToSend)) handleProductImpressions()
  }, [
    viewHandle,
    fetchVariantsForSkus,
    pushProductImpressions,
    skusSent,
    skusViewed,
    trackBatchNumber,
  ])
}

export const CollectionViewProductImpressions = (props: Props) => {
  useCollectionViewProductImpressions(props)
  return null
}
